import { Inject, Injectable, Injector } from '@angular/core';

import { Observable, filter, forkJoin, map, of } from 'rxjs';

import { BetslipError } from '../../errors/betslip-error';
import { IExtendedMessage, MESSAGE_FORMATTER_INJECTION_TOKEN, MessageFormatter } from './message-formatters/message-formatter';
import { UnknownErrorMessageFormatter } from './message-formatters/unknown-error-message-formatter';

/**
 * Return the user message for an error.
 */
@Injectable()
export class BetslipErrorMessageFormatterService {
    constructor(
        private injector: Injector,
        @Inject(MESSAGE_FORMATTER_INJECTION_TOKEN) private messageFormatters: MessageFormatter<BetslipError>[],
    ) {}

    /**
     * Get error message. If error is already translated (for example after BPS it is translated on server)
     * return translation.
     * Try to get specific error message formatter.
     *
     * @param error
     */
    getErrorMessage(error: BetslipError): Observable<string | IExtendedMessage | null> {
        try {
            const formatter = this.getErrorMessageFormatter(error);

            return formatter.format(error);
        } catch (e) {
            console.error(e);

            return of(null);
        }
    }

    getErrorsMessages(errors: BetslipError[]): Observable<string[]> {
        try {
            if (errors.length > 0) {
                return forkJoin(
                    errors.map((err) =>
                        this.getErrorMessage(err).pipe(
                            filter((val) => !!val),
                            map((val) => (typeof val === 'string' ? val : val!.message)),
                        ),
                    ),
                );
            }

            return of([]);
        } catch (e) {
            console.error(e);

            return of([]);
        }
    }

    getErrorMessageFormatter(error: BetslipError): MessageFormatter<BetslipError> {
        for (const messageFormatter of this.messageFormatters) {
            if (messageFormatter.canHandle(error)) {
                return messageFormatter;
            }
        }

        return this.injector.get(UnknownErrorMessageFormatter);
    }
}
